import { strictify } from '~/utils/strictify'
import type {
  PasswordSettingsUpdateRequest,
  TwoFactorChallengeRequest,
  TwoFactorSettingsConfirmRequest,
  UserConfirmTwoFactorRequest,
  UserAccessLogoutOtherSessionsRequest
} from '~/services/api/generated'
import { pendingTwoFactorChallenge } from '~/utils/pendingTwoFactorChallenge'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'
import {
  PasswordConfirmationStoreSuccessSchema,
  PasswordSettingsUpdateSuccessSchema,
  TwoFactorChallengeSuccessSchema,
  TwoFactorSettingsConfirmSuccessSchema,
  TwoFactorSettingsDisableSuccessSchema,
  TwoFactorSettingsEnableSuccessSchema,
  TwoFactorSettingsQrCodeSuccessSchema,
  TwoFactorSettingsRecoveryCodesSuccessSchema,
  UserConfirmTwoFactorSuccessSchema,
  UserAccessLogoutOtherSessionsSuccessSchema
} from '~/services/api/generated'
import { TwoFactorSettingsConfirm } from '~/services/api/contracts/requests/TwoFactorSettingsConfirm'
import { PasswordSettingsUpdate } from '~/services/api/contracts/requests/PasswordSettingsUpdate'
import { TwoFactorChallenge } from '~/services/api/contracts/requests/TwoFactorChallenge'
import { UserAccessLogoutOtherSessions } from '~/services/api/contracts/requests/UserAccessLogoutOtherSessions'
import { UserConfirmTwoFactor } from '~/services/api/contracts/requests/UserConfirmTwoFactor'
import { PasswordConfirmationStore } from '~/services/api/contracts/requests/PasswordConfirmationStore'

export function useSettingsService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api`
  const sanctumClient = useApiRequest()

  /**
   * Change authenticated user password
   */
  async function changePassword(payload: PasswordSettingsUpdateRequest) {
    const request = PasswordSettingsUpdate({}).parse(payload)
    return strictify(PasswordSettingsUpdateSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/password`, { method: 'PUT', body: request })
    )
  }

  /**
   * Log out other sessions
   */
  async function logoutOtherSessions(payload: UserAccessLogoutOtherSessionsRequest) {
    const request = UserAccessLogoutOtherSessions({}).parse(payload)
    return strictify(UserAccessLogoutOtherSessionsSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/logout-other-sessions`, {
        method: 'POST',
        body: request
      })
    )
  }

  /**
   * 2FA: Enable
   */
  async function enableTwoFactor() {
    return strictify(TwoFactorSettingsEnableSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/two-factor-authentication`, { method: 'POST' })
    )
  }

  /**
   * 2FA: Get QR code in SVG
   */
  async function getTwoFactorQrCode() {
    return strictify(TwoFactorSettingsQrCodeSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/two-factor-qr-code`, { method: 'GET' })
    ).data
  }

  /**
   * 2FA: Confirm activation (required to complete setup)
   */
  async function confirmTwoFactor(payload: TwoFactorSettingsConfirmRequest) {
    const request = TwoFactorSettingsConfirm({}).parse(payload)
    return strictify(TwoFactorSettingsConfirmSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/confirmed-two-factor-authentication`, {
        method: 'POST',
        body: request
      })
    )
  }

  /**
   * 2FA: Disable
   */
  async function disableTwoFactor() {
    return strictify(TwoFactorSettingsDisableSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/two-factor-authentication`, { method: 'DELETE' })
    )
  }

  /**
   * 2FA: Get recovery codes
   */
  async function getRecoveryCodes() {
    return strictify(TwoFactorSettingsRecoveryCodesSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/two-factor-recovery-codes`, { method: 'GET' })
    ).data
  }

  /**
   * Confirm password
   */
  async function confirmPassword(password: string) {
    const request = PasswordConfirmationStore({}).parse({ password })
    return strictify(PasswordConfirmationStoreSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/confirm-password`, { method: 'POST', body: request })
    )
  }

  /**
   * 2FA: Complete login challenge. `challenge_token` and `remember` come from
   * the transient handoff `login()` left in memory, not from the caller.
   */
  async function twoFactorChallenge(payload: Omit<TwoFactorChallengeRequest, 'challenge_token'>) {
    const pending = pendingTwoFactorChallenge.peek()
    const request = TwoFactorChallenge({}).parse({
      ...payload,
      challenge_token: pending?.challengeToken
    })
    const response = strictify(TwoFactorChallengeSuccessSchema).parse(
      await sanctumClient(`${baseURL}/two-factor-challenge`, {
        method: 'POST',
        body: request
      })
    )
    // The bearer token only arrives here, not from the initial login call.
    await sanctumTokenStorage.set(useNuxtApp(), response.data.token, pending?.remember ?? false)
    pendingTwoFactorChallenge.clear()
  }

  /**
   * 2FA step-up: re-verify a live TOTP or recovery code to unlock the
   * financial-routing / permission-changing operations gated by the API's
   * `mfa.confirm` middleware. Stamps the session for a short window so those
   * requests stop returning STEP_UP_REQUIRED (HTTP 423).
   */
  async function confirmStepUp(payload: UserConfirmTwoFactorRequest) {
    const request = UserConfirmTwoFactor({}).parse(payload)
    return strictify(UserConfirmTwoFactorSuccessSchema).parse(
      await sanctumClient(`${baseURL}/user/confirm-two-factor`, {
        method: 'POST',
        body: request
      })
    )
  }

  return {
    changePassword,
    logoutOtherSessions,
    enableTwoFactor,
    getTwoFactorQrCode,
    confirmTwoFactor,
    disableTwoFactor,
    getRecoveryCodes,
    confirmPassword,
    twoFactorChallenge,
    confirmStepUp
  }
}
